import type * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Pagination from '@cloudscape-design/components/pagination';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { AuditAction } from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { formatDateTime } from '@/lib/format';
import { AUDIT_LIST_LIMIT, type AuditEventRecord } from '@/lib/firestoreAudit';
import { useAuditEvents } from '@/features/audit/useAuditEvents';

const PAGE_SIZE = 10;

/**
 * Payload gravado em `audit.after` por `broadcastToOwners`. O schema do audit
 * tipa `after` como `unknown`, então revalidamos aqui com Zod (borda de leitura
 * do Firestore — regra do projeto). `messageId` só existe nos eventos de antes
 * da #767 (envio por tópico); os novos trazem contagens (`sent`/`failed`…),
 * que a tabela não exibe — por isso não entram no schema.
 */
const BroadcastAfterSchema = z.object({
  title: z.string(),
  body: z.string(),
  link: z.string().nullable().optional(),
  messageId: z.string().optional(),
  sentAt: z.number().optional(),
});

type BroadcastRow = {
  id: string;
  sentAtMillis: number | null;
  actorEmail: string;
  title: string;
  body: string;
  link: string | null;
};

/**
 * Converte um evento de audit em linha da tabela, ou `null` se o `after` não
 * tem o formato esperado. Pura: quem reporta a falha é o componente, num
 * `useEffect` (efeito colateral fora do `useMemo`).
 */
function toRow(e: AuditEventRecord): BroadcastRow | null {
  const parsed = BroadcastAfterSchema.safeParse(e.after);
  if (!parsed.success) return null;
  return {
    id: e.id,
    sentAtMillis: parsed.data.sentAt ?? e.atMillis,
    actorEmail: e.actorEmail,
    title: parsed.data.title,
    body: parsed.data.body,
    link: parsed.data.link ?? null,
  };
}

/** Busca textual client-side sobre título, mensagem, link e ator. */
function matchesSearch(r: BroadcastRow, text: string): boolean {
  const q = text.trim().toLowerCase();
  if (q === '') return true;
  return (
    r.title.toLowerCase().includes(q) ||
    r.body.toLowerCase().includes(q) ||
    (r.link ?? '').toLowerCase().includes(q) ||
    r.actorEmail.toLowerCase().includes(q)
  );
}

/**
 * Histórico dos pushes enviados, com busca. Fonte: log de auditoria
 * (`action = BROADCAST_OWNERS_SENT`) — o callable `broadcastToOwners` grava
 * cada disparo lá. Espelha o padrão de tabela de `AuditList`.
 *
 * Origem: sem issue — commit f33ddae1f, 2026-06-11.
 *
 * Usado por: `NotificationsBroadcastPage` (`apps/admin`, rota
 * `/notifications/broadcast`). Spec: `docs/screens/admin-notifications-broadcast.md`.
 *
 * Dados: `audit/{id}.after` em dois formatos — antes da #767
 * `{ topic, title, body, link, messageId, sentAt }`; depois
 * `{ title, body, link, sentAt, owners, tokens, sent, failed, pruned }`. O
 * schema não é strict, então os dois parseiam.
 *
 * Histórico:
 *  - 2026-09-28 17:57 +0200 — (#767) falha de carga e evento fora do formato
 *    reportados ao PostHog (`admin_broadcast_history_load_failed` /
 *    `_parse_failed`), não mais só `console.warn`.
 */
export function BroadcastHistory(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const filters = useMemo(() => ({ action: AuditAction.BROADCAST_OWNERS_SENT }), []);
  const state = useAuditEvents(filters);

  // Telemetria (#767, 2026-09-28 17:57 +0200): falha de carga e evento com
  // `after` fora do formato iam só para `console`/toast — em produção não
  // vemos o console do navegador, então a linha simplesmente sumia da tabela
  // sem ninguém saber. Agora os dois vão ao PostHog via `reportNonFatal`.
  useEffect(() => {
    if (state.kind === 'ERROR') {
      toast.error(t('notifications.broadcast.history.errorLoad'));
      reportNonFatal('admin_broadcast_history_load_failed', serializeError(state.error));
    }
  }, [state, t]);

  const { rows, invalidIds } = useMemo(() => {
    if (state.kind !== 'READY') return { rows: [] as BroadcastRow[], invalidIds: [] as string[] };
    const out: BroadcastRow[] = [];
    const invalid: string[] = [];
    for (const e of state.result.events) {
      const row = toRow(e);
      if (row) out.push(row);
      else invalid.push(e.id);
    }
    return { rows: out, invalidIds: invalid };
  }, [state]);

  useEffect(() => {
    if (invalidIds.length > 0) {
      // Só os ids (doc id do audit, sem dado de cliente) e a contagem.
      reportNonFatal('admin_broadcast_history_parse_failed', {
        count: invalidIds.length,
        eventIds: invalidIds.slice(0, 10),
      });
    }
  }, [invalidIds]);
  const truncated = state.kind === 'READY' && state.result.truncated;

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'sentAt',
        header: t('notifications.broadcast.history.columns.sentAt'),
        sortingComparator: (a: BroadcastRow, b: BroadcastRow): number =>
          (a.sentAtMillis ?? 0) - (b.sentAtMillis ?? 0),
        cell: (r: BroadcastRow): React.ReactNode =>
          r.sentAtMillis != null ? formatDateTime(r.sentAtMillis, lang) : '—',
      },
      {
        id: 'title',
        header: t('notifications.broadcast.history.columns.title'),
        cell: (r: BroadcastRow): React.ReactNode => r.title,
      },
      {
        id: 'body',
        header: t('notifications.broadcast.history.columns.body'),
        cell: (r: BroadcastRow): React.ReactNode => r.body,
      },
      {
        id: 'link',
        header: t('notifications.broadcast.history.columns.link'),
        cell: (r: BroadcastRow): React.ReactNode =>
          r.link != null ? (
            <Link href={r.link} external>
              {r.link}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        id: 'actor',
        header: t('notifications.broadcast.history.columns.actor'),
        cell: (r: BroadcastRow): React.ReactNode => (
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.actorEmail}</span>
        ),
      },
    ],
    [t, lang],
  );

  const { items, collectionProps, filterProps, paginationProps, filteredItemsCount } =
    useCollection(rows, {
      filtering: { filteringFunction: matchesSearch },
      sorting: {},
      pagination: { pageSize: PAGE_SIZE },
    });

  return (
    <Container
      header={
        <Header
          counter={`(${String(filteredItemsCount ?? rows.length)})`}
          description={
            truncated
              ? t('notifications.broadcast.history.truncated', { limit: AUDIT_LIST_LIMIT })
              : t('notifications.broadcast.history.cardHint')
          }
        >
          {t('notifications.broadcast.history.cardTitle')}
        </Header>
      }
    >
      <Table<BroadcastRow>
        {...collectionProps}
        items={items}
        columnDefinitions={columnDefinitions}
        trackBy="id"
        variant="embedded"
        loading={state.kind === 'LOADING'}
        loadingText={t('common.loading')}
        empty={
          <Box textAlign="center" color="inherit">
            {t('notifications.broadcast.history.empty')}
          </Box>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder={t('notifications.broadcast.history.search')}
            filteringAriaLabel={t('notifications.broadcast.history.search')}
          />
        }
        pagination={<Pagination {...paginationProps} />}
      />
    </Container>
  );
}
